import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Loader2, RefreshCw, Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function PCCMContributionMargins({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/pccm/contribution-margins`);
      setData(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>;
  if (!data || data.totalItems === 0) return <div className="text-center py-16 text-sm" style={{ color: 'var(--text-muted)' }}>No data available.</div>;

  return (
    <div className="space-y-4" data-testid="pccm-contribution-margins">
      {/* Overall Summary */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ background: '#EF444420', border: '1px solid #EF444440' }}>
            <Scale className="w-5 h-5" style={{ color: '#EF4444' }} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Weighted Progress by System</h3>
            <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{data.totalItems} items across {data.systems.length} systems</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold" style={{ color: '#10b981' }}>{data.overallProgress?.toFixed(1)}%</div>
          <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Overall Weighted Progress</p>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)' }}>
              <th className="p-3 text-left font-medium" style={{ color: 'var(--text-muted)' }}>System</th>
              <th className="p-3 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Items</th>
              <th className="p-3 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Weight %</th>
              <th className="p-3 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Avg Progress</th>
              <th className="p-3 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Progress Bar</th>
              <th className="p-3 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Weighted %</th>
              <th className="p-3 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Done</th>
              <th className="p-3 text-center font-medium" style={{ color: 'var(--text-muted)' }}>Overdue</th>
            </tr>
          </thead>
          <tbody>
            {data.systems.map(s => (
              <tr key={s.systemCode} style={{ borderTop: '1px solid var(--border-color)', background: 'var(--card-bg)' }}>
                <td className="p-3">
                  <span className="font-mono text-xs font-semibold" style={{ color: 'var(--accent-primary)' }}>{s.systemCode}</span>
                  <span className="text-xs ml-2" style={{ color: 'var(--text-secondary)' }}>{s.systemName}</span>
                </td>
                <td className="p-3 text-center" style={{ color: 'var(--text-primary)' }}>{s.itemCount}</td>
                <td className="p-3 text-center" style={{ color: 'var(--text-muted)' }}>{s.weightPercent?.toFixed(1)}%</td>
                <td className="p-3 text-center" style={{ color: 'var(--text-primary)' }}>{s.averageProgress?.toFixed(1)}%</td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: 'var(--border-color)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(s.averageProgress, 100)}%`, background: '#10b981' }} />
                    </div>
                  </div>
                </td>
                <td className="p-3 text-center font-semibold" style={{ color: '#10b981' }}>{s.weightedProgress?.toFixed(2)}%</td>
                <td className="p-3 text-center" style={{ color: '#22c55e' }}>{s.completed}</td>
                <td className="p-3 text-center" style={{ color: '#ef4444' }}>{s.overdue}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ borderTop: '2px solid var(--border-color)', background: 'var(--card-bg-alt)' }}>
              <td className="p-3 font-semibold" style={{ color: 'var(--text-primary)' }}>Total</td>
              <td className="p-3 text-center font-semibold" style={{ color: 'var(--text-primary)' }}>{data.totalItems}</td>
              <td className="p-3 text-center font-semibold" style={{ color: 'var(--text-primary)' }}>100%</td>
              <td className="p-3" />
              <td className="p-3" />
              <td className="p-3 text-center font-bold text-base" style={{ color: '#10b981' }}>{data.overallProgress?.toFixed(2)}%</td>
              <td className="p-3 text-center font-semibold" style={{ color: '#22c55e' }}>{data.systems.reduce((a, s) => a + s.completed, 0)}</td>
              <td className="p-3 text-center font-semibold" style={{ color: '#ef4444' }}>{data.systems.reduce((a, s) => a + s.overdue, 0)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
